import React, { useEffect, useState } from 'react';
import { suppliersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Truck, CheckCircle, AlertTriangle, Clock, Ban,
  RefreshCw, Star, ArrowRight, Timer, Play, PauseCircle,
  ClipboardCheck, FileText, TrendingUp, Calendar
} from 'lucide-react';

export default function SupplierDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isPurchaseManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';

  useEffect(() => {
    loadStats();
  }, [location.key]);

  const loadStats = async () => {
    try {
      const res = await suppliersAPI.dashboard();
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load supplier dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDue = async () => {
    setChecking(true);
    try {
      const res = await suppliersAPI.checkReevaluationDue();
      toast.success(res.data.message || 'Reevaluation check completed');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run check');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="supplier-dashboard-loading">
        <div className="text-sm text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  const cards = [
    { label: 'Total Suppliers', value: stats?.total || 0, icon: Truck, color: 'text-[#0F172A]', bg: 'bg-white', link: '/suppliers/list' },
    { label: 'Approved', value: stats?.approved || 0, icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50', link: '/suppliers/list?status=Approved' },
    { label: 'Created', value: stats?.created || 0, icon: FileText, color: 'text-slate-600', bg: 'bg-slate-100', link: '/suppliers/list?status=Created' },
    { label: 'Under Review', value: stats?.under_reevaluation || 0, icon: RefreshCw, color: 'text-blue-700', bg: 'bg-blue-50', link: '/suppliers/list?status=Under re-evaluation' },
    { label: 'Due Soon (15d)', value: stats?.due_soon || 0, icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50', link: '/suppliers/list?status=Approved' },
    { label: 'Due This Month', value: stats?.due_this_month || 0, icon: Calendar, color: 'text-indigo-700', bg: 'bg-indigo-50', link: '/suppliers/list?status=Approved' },
    { label: 'Overdue', value: stats?.overdue || 0, icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-50', link: '/suppliers/list?status=Overdue' },
    { label: 'On Hold', value: stats?.on_hold || 0, icon: PauseCircle, color: 'text-orange-700', bg: 'bg-orange-50', link: '/suppliers/list?status=On Hold' },
    { label: 'No Longer Needed', value: stats?.no_longer_needed || 0, icon: Ban, color: 'text-slate-500', bg: 'bg-slate-100', link: '/suppliers/list?status=No Longer Needed' },
  ];

  return (
    <div data-testid="supplier-dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">Supplier Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Supplier management overview</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="supplier-stats-grid">
        {cards.map((card, i) => (
          <Card
            key={i}
            className={`border border-slate-200 shadow-none transition-shadow ${card.link ? 'cursor-pointer hover:shadow-md hover:border-slate-300' : ''}`}
            onClick={card.link ? () => navigate(card.link) : undefined}
            data-testid={`stat-card-${card.label.toLowerCase().replace(/[\s()]/g, '-')}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-md ${card.bg} border ${card.color === 'text-[#0F172A]' ? 'border-slate-200' : ''} flex items-center justify-center`}>
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight">{card.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Reviews + Average Rating */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Pending Reviews */}
        <Card className="border border-slate-200 shadow-none" data-testid="pending-reviews-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> Pending Reviews
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Purchase Manager Reviews</span>
              <Badge variant="outline" className={`text-[10px] ${stats?.pending_purchase_reviews > 0 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                {stats?.pending_purchase_reviews || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Quality Manager Reviews</span>
              <Badge variant="outline" className={`text-[10px] ${stats?.pending_quality_reviews > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                {stats?.pending_quality_reviews || 0}
              </Badge>
            </div>
            {(stats?.pending_purchase_reviews > 0 || stats?.pending_quality_reviews > 0) && (
              <Button
                onClick={() => navigate('/suppliers/list?status=Under re-evaluation')}
                variant="outline"
                size="sm"
                className="w-full text-xs gap-1.5 mt-2"
                data-testid="view-pending-reviews-btn"
              >
                <ArrowRight className="w-3 h-3" /> View Pending Suppliers
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Average Rating */}
        <Card className="border border-slate-200 shadow-none" data-testid="avg-rating-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" /> Performance Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="text-center py-2">
              <p className="text-3xl font-bold text-[#0F172A]" data-testid="avg-sr-value">
                {stats?.avg_rating != null ? stats.avg_rating : '—'}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">Average Supplier Rating</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Rated Suppliers</span>
              <span className="text-xs font-semibold text-[#0F172A]">{stats?.rated_count || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Scheduler Status */}
        <Card className="border border-slate-200 shadow-none" data-testid="supplier-scheduler-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Timer className="w-4 h-4" /> Auto-Check Scheduler
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Status</span>
              {stats?.scheduler?.active ? (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Schedule</span>
              <span className="text-xs font-mono text-[#0F172A]">{stats?.scheduler?.schedule || 'Daily 06:30 UTC'}</span>
            </div>
            {stats?.scheduler?.next_run && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Next Run</span>
                <span className="text-xs font-mono text-[#0F172A]">{stats.scheduler.next_run.replace('T', ' ').substring(0, 19)}</span>
              </div>
            )}
            <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-100">
              Automatically starts reevaluation for suppliers due within 15 days
            </p>
            {isPurchaseManager && (
              <Button
                onClick={handleCheckDue}
                disabled={checking}
                variant="outline"
                size="sm"
                className="w-full text-xs gap-1.5 mt-1"
                data-testid="manual-reeval-check-button"
              >
                <Play className="w-3 h-3" />
                {checking ? 'Running...' : 'Run Check Now'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Reevaluations */}
      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Recent Reevaluations
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/suppliers/list')}
              className="text-xs text-slate-500 gap-1"
              data-testid="view-all-suppliers-button"
            >
              View All Suppliers <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {stats?.recent_reevaluations?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_reevaluations.map((item, i) => (
                <div 
                  key={i} 
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 rounded px-2 -mx-2"
                  onClick={() => navigate(`/suppliers/${item.id}`)}
                  data-testid={`recent-reeval-${i}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{item.supplier_name}</p>
                    <p className="text-xs text-slate-400 font-mono">{item.supplier_id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {item.supplier_rating != null && (
                      <span className={`text-sm font-bold ${item.supplier_rating >= 70 ? 'text-emerald-600' : item.supplier_rating >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                        SR: {item.supplier_rating}
                      </span>
                    )}
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] ${
                        item.final_status === 'Approved' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 
                        item.final_status === 'On Hold' ? 'border-amber-200 text-amber-700 bg-amber-50' :
                        'border-red-200 text-red-700 bg-red-50'
                      }`}
                    >
                      {item.final_status}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{item.evaluated_at?.substring(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-4 text-center">No reevaluations completed yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
